import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Clock } from "lucide-react";

const PILLARS = ["ALL", "SOURCE", "CONSCIOUSNESS", "ENERGY", "FREQUENCY", "TIMELINES"];

export default function Library() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [active, setActive] = useState(null);

  useEffect(() => {
    api
      .get("/library")
      .then((r) => setItems(r.data.teachings))
      .catch((err) => console.error("Failed to load library:", err));
  }, []);

  const filtered = filter === "ALL" ? items : items.filter((i) => i.pillar === filter);

  return (
    <div className="pt-20 sm:pt-24" data-testid="library-page">
      <section className="mx-auto max-w-7xl px-5 sm:px-6 py-10 sm:py-16">
        <div className="eyebrow mb-4">The Library</div>
        <h1 className="font-serif text-[2.25rem] leading-[1.05] sm:text-6xl sm:leading-tight mb-5 sm:mb-6 max-w-3xl">
          Thirteen teachings on the architecture of <span className="italic text-accent">becoming</span>.
        </h1>
        <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mb-8 sm:mb-10 leading-relaxed">
          The living body of thought behind The Shift. Read one. Let it work on you.
          Come back tomorrow for the next.
        </p>

        <div
          className="flex gap-2 mb-8 sm:mb-10 overflow-x-auto -mx-5 px-5 sm:mx-0 sm:px-0 sm:flex-wrap pb-2 sm:pb-0 scrollbar-none"
          style={{ scrollbarWidth: "none" }}
          data-testid="library-filters"
        >
          {PILLARS.map((p) => (
            <button
              key={p}
              onClick={() => setFilter(p)}
              className={`shrink-0 px-4 h-10 flex items-center font-mono text-[10px] tracking-[0.28em] sm:tracking-[0.32em] uppercase transition-colors border whitespace-nowrap ${
                filter === p ? "border-accent text-accent bg-accent/10" : "border-border text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`library-filter-${p.toLowerCase()}`}
            >
              {p}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border/40">
          {filtered.map((t, i) => (
            <motion.button
              key={t.slug}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: i * 0.02 }}
              onClick={() => setActive(t)}
              className="bg-background p-6 sm:p-8 text-left hover:bg-secondary/40 active:bg-secondary/60 transition-colors group min-h-[180px]"
              data-testid={`library-card-${t.slug}`}
            >
              <div className="flex items-center justify-between mb-5 sm:mb-6">
                <Badge variant="outline" className="font-mono text-[9px] tracking-widest">
                  {t.pillar}
                </Badge>
                <div className="flex items-center gap-1 font-mono text-[10px] text-muted-foreground tracking-widest">
                  <Clock className="h-3 w-3" /> {t.read_time} min
                </div>
              </div>
              <h3 className="font-serif text-xl sm:text-3xl mb-2 sm:mb-3 group-hover:text-accent transition-colors leading-snug">
                {t.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{t.excerpt}</p>
              <div className="mt-5 sm:mt-6 flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase text-accent">
                <BookOpen className="h-3 w-3" /> Read
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-5 sm:p-6" data-testid="library-reader-sheet">
          {active && (
            <>
              <SheetHeader className="mb-5 sm:mb-6 text-left">
                <div className="eyebrow mb-2">{active.pillar} • {active.read_time} min</div>
                <SheetTitle className="font-serif text-2xl sm:text-5xl leading-tight text-left">
                  {active.title}
                </SheetTitle>
                <SheetDescription className="font-serif italic text-base sm:text-lg text-left text-muted-foreground">
                  {active.excerpt}
                </SheetDescription>
              </SheetHeader>
              <div className="hairline pt-5 sm:pt-6 font-serif text-base sm:text-lg leading-relaxed text-foreground">
                {active.body}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
